import type { ComponentType } from "react";
import { can, type Role } from "@shared/domain";
import {
  BillListIcon,
  BoxIcon,
  BuildingsIcon,
  CalendarIcon,
  CardIcon,
  ChartSquareIcon,
  ChatRoundLineIcon,
  ChecklistMinimalisticIcon,
  ChefHatIcon,
  ClipboardCheckIcon,
  ClockCircleIcon,
  DangerTriangleIcon,
  DeliveryIcon,
  ForbiddenCircleIcon,
  HistoryIcon,
  KeyMinimalisticIcon,
  LockPasswordIcon,
  MonitorIcon,
  RefreshCircleIcon,
  ShieldUserIcon,
  SignpostIcon,
  SledgehammerIcon,
  Sun2Icon,
  TagPriceIcon,
  TShirtIcon,
  WashingMachineIcon,
  CalendarMarkIcon,
  EyeIcon,
  Tuning2Icon,
  UsersGroupRoundedIcon,
  WalletMoneyIcon,
  Widget2Icon,
} from "../icons";

type Icon = ComponentType<{ size?: number; className?: string }>;

export type NavItem = {
  to: string;
  label: string;
  icon: Icon;
  visible: (role: Role) => boolean;
};

export type AreaId =
  | "today"
  | "desk"
  | "checks"
  | "repairs"
  | "laundry"
  | "transactions"
  | "reports";

export type Area = {
  id: AreaId;
  /** The area's own address: its prefix, and where a bare link lands. */
  to: string;
  label: string;
  /** The phone's bottom bar. */
  short?: string;
  /** Staff see their own checks under another name. */
  staffLabel?: string;
  icon: Icon;
  /** Sub-tabs, shown at the top of the page. */
  sections?: NavItem[];
};

const all = () => true;

/*
 * Seven areas, left to right. Nothing is greyed: a section the role can't
 * use just isn't there, and an area left with one section becomes it.
 * Settings is not an area: it opens from the account menu (and More on a phone).
 */
export const AREAS: Area[] = [
  { id: "today", to: "/", label: "Today", icon: Sun2Icon },
  {
    id: "desk",
    to: "/desk",
    label: "Front desk",
    short: "Desk",
    icon: CalendarIcon,
    sections: [
      {
        to: "/desk/calendar",
        label: "Calendar",
        icon: CalendarIcon,
        visible: all,
      },
      {
        to: "/desk/conflicts",
        label: "Conflicts",
        icon: DangerTriangleIcon,
        visible: all,
      },
      {
        to: "/desk/guests",
        label: "Guests",
        icon: UsersGroupRoundedIcon,
        visible: all,
      },
    ],
  },
  // Staff see one section, their own checks: no rail, and the area wears "My checks".
  {
    id: "checks",
    to: "/checks",
    label: "Room checks",
    short: "Checks",
    staffLabel: "My checks",
    icon: ClipboardCheckIcon,
    sections: [
      { to: "/checks", label: "Plan", icon: CalendarMarkIcon, visible: all },
      {
        to: "/checks/review",
        label: "Review",
        icon: EyeIcon,
        visible: (r) => can({ role: r }, "upkeep.assign"),
      },
      {
        to: "/checks/checklists",
        label: "Checklists",
        icon: ChecklistMinimalisticIcon,
        visible: (r) => can({ role: r }, "upkeep.setup"),
      },
      {
        to: "/checks/rules",
        label: "Rules",
        icon: Tuning2Icon,
        visible: (r) => can({ role: r }, "upkeep.setup"),
      },
    ],
  },
  { id: "repairs", to: "/repairs", label: "Repairs", icon: SledgehammerIcon },
  {
    id: "laundry",
    to: "/laundry",
    label: "Laundry",
    icon: TShirtIcon,
    sections: [
      { to: "/laundry/stock", label: "Stock", icon: TShirtIcon, visible: all },
      {
        to: "/laundry/batches",
        label: "Batches",
        icon: WashingMachineIcon,
        visible: all,
      },
      {
        to: "/laundry/vendors",
        label: "Vendors & bill",
        icon: DeliveryIcon,
        visible: (r) => can({ role: r }, "money.view"),
      },
    ],
  },
  {
    id: "transactions",
    to: "/transactions",
    label: "Transactions",
    short: "Money",
    icon: WalletMoneyIcon,
    sections: [
      {
        to: "/transactions/rates",
        label: "Rates & inventory",
        icon: TagPriceIcon,
        visible: (r) => can({ role: r }, "rates.manage"),
      },
      {
        to: "/transactions/spend",
        label: "Spend",
        icon: WalletMoneyIcon,
        visible: all,
      },
    ],
  },
  // Owner only: navFor() drops the area for everyone else.
  {
    id: "reports",
    to: "/reports",
    label: "Reports",
    icon: ChartSquareIcon,
    sections: [
      {
        to: "/reports",
        label: "Overview",
        icon: Widget2Icon,
        visible: (r) => r === "owner",
      },
      {
        to: "/reports/performance",
        label: "Occupancy & revenue",
        icon: ChartSquareIcon,
        visible: (r) => r === "owner",
      },
      {
        to: "/reports/sources",
        label: "Bookings by source",
        icon: SignpostIcon,
        visible: (r) => r === "owner",
      },
      {
        to: "/reports/cancellations",
        label: "Cancellations",
        icon: ForbiddenCircleIcon,
        visible: (r) => r === "owner",
      },
    ],
  },
];

/** An area's name for this role: staff see "My checks". */
export const areaLabel = (a: Area, role: Role) =>
  a.staffLabel && !can({ role }, "upkeep.assign") ? a.staffLabel : a.label;

export type SettingsGroup = "This property" | "Selling" | "Team" | "You";

export type SettingsSection = NavItem & {
  id: string;
  group: SettingsGroup;
  /** The one-liner under the item on phones, and the section's lede. */
  note: string;
  /** Small print under the panels. */
  foot?: string;
  /** Who may change it; a viewer without this is told who can. Absent: whoever sees it. */
  edit?: { can: (role: Role) => boolean; who: string };
  /** Follows the property picked at the top of the list. */
  perProperty?: boolean;
  /** One setting for every property; the eyebrow says so. */
  business?: boolean;
  /** A table that needs the whole column. */
  wide?: boolean;
  /** One-line heading (a working screen): 20px title, kicker beside it, lede under. */
  compact?: boolean;
};

const MANAGERS = "Only a manager or the owner can change these.";

export const SETTINGS: SettingsSection[] = [
  {
    id: "general",
    to: "/settings/general",
    group: "This property",
    label: "Stay rules",
    icon: ClockCircleIcon,
    visible: all,
    edit: { can: (r) => can({ role: r }, "settings.manage"), who: MANAGERS },
    perProperty: true,
    note: "Check-in and check-out times, how long holds last, the OTA buffer.",
    foot: "A hold keeps a room for a WhatsApp or phone guest until it lapses. Past the buffer threshold, the last rooms stop being offered to the OTAs.",
  },
  {
    id: "messages",
    to: "/settings/messages",
    group: "This property",
    label: "Guest messages",
    icon: ChatRoundLineIcon,
    visible: (r) => can({ role: r }, "settings.manage"),
    perProperty: true,
    note: "The WhatsApp message a guest is sent when their booking is confirmed or held.",
    foot: "Anyone taking a booking can still change the wording for one guest before sending it.",
  },
  {
    id: "rooms",
    to: "/settings/rooms",
    group: "This property",
    label: "Rooms & types",
    icon: BuildingsIcon,
    visible: all,
    edit: {
      can: (r) => can({ role: r }, "properties.manage"),
      who: "Only the owner can change these.",
    },
    perProperty: true,
    note: "The property, its room types and rooms.",
  },
  {
    id: "access",
    to: "/settings/access",
    group: "This property",
    label: "Access & lockboxes",
    icon: KeyMinimalisticIcon,
    visible: (r) => can({ role: r }, "access.rotate"),
    perProperty: true,
    wide: true,
    compact: true,
    note: "One code per room. Every change is kept.",
  },
  {
    id: "taxes",
    to: "/settings/taxes",
    group: "This property",
    label: "Taxes",
    icon: BillListIcon,
    visible: (r) => can({ role: r }, "settings.manage"),
    perProperty: true,
    note: "What every new stay here is charged on room, meals and extras.",
    foot: "Changes apply to stays booked from now on. Bookings already taken keep the tax they were priced with.",
  },
  {
    id: "extras",
    to: "/settings/extras",
    group: "This property",
    label: "Meals & extras",
    icon: ChefHatIcon,
    visible: (r) => can({ role: r }, "rates.manage"),
    perProperty: true,
    note: "Meal plans and extras, their prices, and the colour each wears on bookings.",
  },
  {
    id: "payments",
    to: "/settings/payments",
    group: "Selling",
    label: "Payment methods",
    icon: CardIcon,
    visible: (r) => can({ role: r }, "money.view"),
    edit: { can: (r) => can({ role: r }, "settings.manage"), who: MANAGERS },
    perProperty: true,
    note: "How guests pay, and which of those this property takes.",
    foot: "Recording a payment or a refund offers only the methods the booking's property takes.",
  },
  {
    id: "sources",
    to: "/settings/sources",
    group: "Selling",
    label: "Booking sources",
    icon: SignpostIcon,
    visible: (r) => can({ role: r }, "settings.manage"),
    business: true,
    note: "Where bookings come from, and the colour each shows on the calendar. Every property.",
  },
  {
    id: "channel",
    to: "/settings/channel",
    group: "Selling",
    label: "Channel manager",
    icon: RefreshCircleIcon,
    visible: (r) => can({ role: r }, "settings.manage"),
    perProperty: true,
    note: "The Aiosell link. It hears one thing from us: how many rooms of each type the platforms may sell each night.",
  },
  {
    id: "users",
    to: "/settings/users",
    group: "Team",
    label: "Users & roles",
    icon: ShieldUserIcon,
    visible: (r) => can({ role: r }, "users.manage"),
    wide: true,
    note: "Invite people, set what they can see and do.",
  },
  {
    id: "activity",
    to: "/settings/activity",
    group: "Team",
    label: "Activity log",
    icon: HistoryIcon,
    visible: all,
    wide: true,
    compact: true,
    note: "Every change, by whom and when. Written once, never edited.",
  },
  {
    id: "security",
    to: "/settings/security",
    group: "You",
    label: "Password & two-factor",
    icon: LockPasswordIcon,
    visible: all,
    note: "Your own sign-in. Nobody else sees these.",
  },
  {
    id: "display",
    to: "/settings/display",
    group: "You",
    label: "Display",
    icon: MonitorIcon,
    visible: all,
    note: "How dates are written on this device. Saved dates are unchanged.",
  },
];

/** Settings sections this role sees, grouped in list order. */
export function settingsFor(
  role: Role,
): { group: SettingsGroup; sections: SettingsSection[] }[] {
  const groups: { group: SettingsGroup; sections: SettingsSection[] }[] = [];
  for (const s of SETTINGS) {
    if (!s.visible(role)) continue;
    const g = groups.find((x) => x.group === s.group);
    if (g) g.sections.push(s);
    else groups.push({ group: s.group, sections: [s] });
  }
  return groups;
}

/**
 * The course bar and the phone's bottom bar: sections filtered by role, empty
 * areas dropped, and an area left with one section wearing that section's name.
 */
export function navFor(role: Role): Area[] {
  return AREAS.flatMap((a) => {
    const named = { ...a, label: areaLabel(a, role) };
    if (!a.sections) return [named];
    const sections = a.sections.filter((s) => s.visible(role));
    if (sections.length === 0) return [];
    // An area left with one section wears its name — unless the area names itself for the role (My checks).
    if (sections.length === 1)
      return [
        a.staffLabel && named.label === a.staffLabel
          ? { ...named, sections }
          : {
              ...named,
              label: sections[0].label,
              short: a.short ?? sections[0].label,
              icon: sections[0].icon,
              sections,
            },
      ];
    return [{ ...named, sections }];
  });
}

/** Everywhere this role can go — what the command palette searches. */
export function destinationsFor(role: Role): NavItem[] {
  const out: NavItem[] = [];
  for (const a of navFor(role)) {
    if (a.sections && a.sections.length > 1)
      out.push(
        ...a.sections.map((s) => ({ ...s, label: `${s.label} · ${a.label}` })),
      );
    else
      out.push({
        to: a.sections?.[0].to ?? a.to,
        label: a.label,
        icon: a.icon,
        visible: all,
      });
  }
  // Pages inside areas that aren't sections of them.
  out.push({
    to: "/lost",
    label: "Lost & found · Today",
    icon: BoxIcon,
    visible: all,
  });
  // Settings is no longer an area, but every section is still somewhere to go.
  out.push(
    ...SETTINGS.filter((s) => s.visible(role)).map((s) => ({
      ...s,
      label: `${s.label} · Settings`,
    })),
  );
  return out;
}

/** The area a path belongs to. /new, /settings and anything unknown belong to none. */
export function areaOf(path: string): AreaId | undefined {
  if (path === "/") return "today";
  // Lost & found is Today's.
  if (path === "/lost" || path.startsWith("/lost/")) return "today";
  return AREAS.find(
    (a) => a.to !== "/" && (path === a.to || path.startsWith(a.to + "/")),
  )?.id;
}

/** The section a path sits in: the longest section prefix, so /desk/calendar/x is Calendar. */
export function sectionOf<T extends { to: string }>(
  sections: T[],
  path: string,
): T | undefined {
  return sections
    .filter((s) => path === s.to || path.startsWith(s.to + "/"))
    .sort((a, b) => b.to.length - a.to.length)[0];
}

/** Where clicking an area goes: the section it was last on, or its first. */
export function areaHref(area: Area, last: Record<string, string>): string {
  const remembered = last[area.id];
  if (!area.sections) return area.to;
  return area.sections.some((s) => s.to === remembered) && remembered
    ? remembered
    : area.sections[0].to;
}
